"use client";
import { ContentMessageType } from "@shared/graphql.types";
import {
  TrustPilotRatingWidget,
  trustPilotConfig
} from "@shared/ui.components";
import { type ColourVariant, colours } from "@shared/ui.design-system";
import { ChevronLeftIcon, ChevronRightIcon } from "@shared/ui.icons";
import { MarkdownContainer, Typography } from "@shared/ui.primitives";
import { getDerivedLocale } from "@shared/utils.locale";
import { navigateTo } from "@shared/utils.routing";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  button,
  dot,
  dots,
  embla,
  emblaContainer,
  emblaSlide,
  emblaViewPort,
  navButtons,
  slideInner
} from "./PromoCarousel.css";
import { PromoCarouselTracking } from "./tracking.types";

type PromoCarouselProps = {
  fontColour: ColourVariant;
  slides: ContentMessageType[];
  tracking?: PromoCarouselTracking;
};

const getDotColor = (isActive: boolean, fontColour: ColourVariant) => {
  if (!isActive) return colours.secondary.light;

  if (fontColour === "primary-dark") return colours.primary.dark;

  return colours.accent.warm;
};

const getTextAndIconsColour = (fontColour: ColourVariant) => {
  if (fontColour === "primary-dark") return colours.primary.dark;

  return colours.secondary.light;
};

/**
 * A promotional carousel component that displays slides with navigation controls and autoplay functionality.
 *
 * @component
 * @example
 * ```tsx
 * <PromoCarousel slides={["Slide 1", "Slide 2", "Slide 3"]} />
 * ```
 *
 * @param props - The component props
 * @param props.slides - Array of strings representing the content of each slide
 * @param props.fontColour - The font colour to be used for the slide text
 *
 * @returns A React functional component that renders a carousel with:
 * - Automatic slide rotation every 7 seconds
 * - Manual navigation with left/right chevron buttons
 * - Dot indicators for direct slide navigation
 * - Loop functionality for continuous scrolling
 * - Autoplay that resets on user interaction
 *
 * @remarks
 * - Uses Embla Carousel for carousel functionality
 * - Autoplay stops on user interaction and resets when navigation is used
 * - Navigation buttons are disabled when at the beginning/end of slides (though loop is enabled)
 * - Each slide is rendered as a paragraph element
 * - Includes z-index containers for proper layering
 */
export const PromoCarousel: React.FC<PromoCarouselProps> = ({
  slides,
  fontColour,
  tracking
}) => {
  const autoplay = useRef(Autoplay({ delay: 7000, stopOnInteraction: false }));
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    autoplay.current
  ]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const locale = getDerivedLocale();

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    trackSlideViewed(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const resetAutoplay = useCallback(() => {
    autoplay.current.reset();
  }, [autoplay]);

  const scrollTo = (index: number) => {
    emblaApi?.scrollTo(index);
    resetAutoplay();
  };

  const slideViewedRef = useRef<Set<string>>(new Set());

  const trackSlideViewed = (slideIndex: number) => {
    const slide = slides[slideIndex];

    if (!slide) return;

    // This manages that each "message viewed" is tracked only once
    // Using combination of type and message to ensure unique tracking
    const uniqueKey = `${slide.type}:${slide.message}`;
    if (!slideViewedRef.current.has(uniqueKey)) {
      slideViewedRef.current.add(uniqueKey);

      tracking?.onMessageViewed({
        location_type: "promotional_banner",
        module_content: slide.type,
        module_content_details: `${slideIndex + 1} of ${slides.length}`,
        module_content_details2: slide.message
      });
    }
  };

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on("select", onSelect);
    emblaApi.on("pointerDown", resetAutoplay);
    onSelect(); // This is needed only for the first time

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("pointerDown", resetAutoplay);
    };
  }, [emblaApi, onSelect, resetAutoplay]);

  const handleTrustPilotPress = useCallback(
    (slide: ContentMessageType, index: number) => {
      tracking?.onMessageClicked({
        location_type: "promotional_banner",
        module_content: slide.type,
        module_content_details: `${index + 1} of ${slides.length}`,
        module_content_url: trustPilotConfig.url[locale]
      });
      const newWin = window.open(trustPilotConfig.url[locale], "_blank");
      if (newWin) {
        // Protect against reverse tabnabbing
        newWin.opener = null; // mimics rel="noopener noreferrer"
      }
    },
    []
  );

  const isFullUrl = (link: string) => {
    try {
      new URL(link);
      return true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      return false;
    }
  };

  const handleSlidePress = useCallback(
    (slide: ContentMessageType, index: number) => {
      if (!slide.link) return;

      if (isFullUrl(slide.link)) {
        const link = slide.link;

        tracking?.onMessageClicked({
          location_type: "promotional_banner",
          module_content: slide.type,
          module_content_details: `${index + 1} of ${slides.length}`,
          module_content_url: link
        });
        window.open(link, "_blank");
      } else {
        const link =
          locale === "en-GB"
            ? `${window?.location.origin}${slide.link}`
            : `${window?.location.origin}/us${slide.link}`;
        tracking?.onMessageClicked({
          location_type: "promotional_banner",
          module_content: slide.type,
          module_content_details: `${index + 1} of ${slides.length}`,
          module_content_url: link
        });
        navigateTo(link);
      }
    },
    []
  );

  return (
    <div
      className={embla}
      data-testid="promo-carousel"
      onClick={() => {
        tracking?.onBannerClicked({
          location_type: "promotional_banner",
          module_content: "promotional_banner"
        });
      }}
    >
      <div className={emblaViewPort} ref={emblaRef}>
        <div className={emblaContainer}>
          {slides.map((slide, index) => {
            if (slide.type === "TRUST_PILOT") {
              return (
                <div
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ")
                      handleTrustPilotPress(slide, index);
                  }}
                  className={emblaSlide}
                  key={"slide-trust-pilot"}
                  data-testid={`slide-${index}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTrustPilotPress(slide, index);
                  }}
                >
                  <div
                    className={slideInner}
                    style={{ position: "relative" }}
                    inert
                  >
                    <TrustPilotRatingWidget
                      textColor={getTextAndIconsColour(fontColour)}
                    />
                  </div>
                </div>
              );
            }
            return (
              <div
                role="link"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    handleSlidePress(slide, index);
                  }
                }}
                className={emblaSlide}
                key={`slide-${slide.type}-${slide.message}`}
                data-testid={`slide-${index}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSlidePress(slide, index);
                }}
              >
                <div className={slideInner}>
                  <MarkdownContainer
                    content={slide.message}
                    forceBlock={true}
                    tagOverrides={{
                      a: {
                        component: Typography,
                        props: {
                          as: "a",
                          target: "_blank",
                          variant: "body-sm",
                          underline: true,
                          colour: fontColour,
                          textAlign: "center"
                        }
                      },
                      h4: {
                        component: Typography,
                        props: {
                          as: "h4",
                          variant: "subheader-4",
                          colour: fontColour,
                          textAlign: "center"
                        }
                      },
                      p: {
                        component: Typography,
                        props: {
                          as: "p",
                          variant: "body-sm",
                          colour: fontColour,
                          textAlign: "center"
                        }
                      },
                      span: {
                        component: Typography,
                        props: {
                          as: "span",
                          variant: "body-sm",
                          colour: fontColour,
                          textAlign: "center"
                        }
                      },
                      b: {
                        component: Typography,
                        props: {
                          as: "strong",
                          variant: "body-sm",
                          colour: fontColour,
                          textAlign: "center"
                        }
                      }
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {slides.length > 1 && (
        <div className={navButtons}>
          <button
            className={button}
            data-testid="chevron-left-icon"
            onClick={(e) => {
              e.stopPropagation();
              emblaApi?.scrollPrev();
              resetAutoplay();
              tracking?.onChevronClicked({
                location_type: "promotional_banner",
                module_content: slides[selectedIndex].type,
                module_content_details: "left",
                module_content_details2: slides[selectedIndex].message
              });
            }}
            disabled={!canScrollPrev}
          >
            <ChevronLeftIcon
              fill={getTextAndIconsColour(fontColour)}
              height={20}
            />
          </button>

          <button
            className={button}
            data-testid="chevron-right-icon"
            onClick={(e) => {
              e.stopPropagation();
              emblaApi?.scrollNext();
              resetAutoplay();
              tracking?.onChevronClicked({
                location_type: "promotional_banner",
                module_content: slides[selectedIndex].type,
                module_content_details: "right",
                module_content_details2: slides[selectedIndex].message
              });
            }}
            disabled={!canScrollNext}
          >
            <ChevronRightIcon
              color={getTextAndIconsColour(fontColour)}
              height={20}
            />
          </button>

          <div className={dots}>
            {slides.map((slide, i) => (
              <button
                key={`dot-${slide.type}-${slide.message}`}
                data-testid="dot"
                className={dot}
                data-active={i === selectedIndex}
                title={`Slide ${i + 1} Dot`}
                onClick={() => scrollTo(i)}
                style={{
                  background: getDotColor(i === selectedIndex, fontColour)
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
